import { View, Image } from "@/components/ui";
import { useRoute } from "@/components/shared/router";
import { bookingRoutes } from "@/components/hooks/use-booking-route";
import { Typography } from "../typography";
import {
  buildLinkWithTrackingUrl,
  sendAnalyticData,
} from "@/lib/monitoring/analytics";
import {
  type BetaServiceItem,
  type ServiceItem,
} from "@/containers/homepage/lib";
import { airconServiceTypeOptions } from "@/constants/booking";
import { useBookingState } from "@/store/booking/useBookingState";
import type {
  BetaServiceName,
  AirconServiceName,
  ServiceName,
} from "@/types/service";
import { StarFour } from "@/components/shared/icons";
import { handleOpenLink } from "@/lib/platform";
import { cn } from "@/lib/utils";
import Colors from "@/constants/colors";

const getNumberOfLines = (title: string) => {
  const titleLength = title.split(" ").length;
  return titleLength >= 2 ? titleLength : 1;
};

function ServiceIconUI({
  icon,
  title,
  onClick,
  className,
}: {
  icon: string;
  title: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <View
      className={cn(
        "relative flex flex-1 cursor-pointer flex-col items-center gap-1",
        className,
        getNumberOfLines(title) === 1 ? "px-0.5" : "px-2",
      )}
      onClick={onClick}
    >
      <View className="relative flex h-12 w-14 flex-row items-end justify-center rounded-2xl md:h-14 md:w-16">
        <StarFour
          weight="fill"
          color={Colors.smalt[50]}
          className="absolute right-0 top-0 z-0 h-10 w-10 md:-mr-2 md:h-12 md:w-12"
        />
        <View className="h-10 w-10 md:h-12 md:w-12">
          <Image src={icon} className="h-full w-full" />
        </View>
      </View>
      <Typography
        variant="caption-md"
        align="center"
        numberOfLines={getNumberOfLines(title)}
        className={"z-50 h-7 whitespace-pre-line font-semibold md:h-8"}
      >
        {title}
      </Typography>
    </View>
  );
}

export function ServiceIcon({
  icon,
  title,
  name,
  link,
  className,
}: ServiceItem & { className?: string }) {
  const { push } = useRoute();
  const {
    data: { packageDetailsFilter },
    setBookingState,
  } = useBookingState();

  const onClickAirconService = (name: AirconServiceName) => {
    const airconService = airconServiceTypeOptions.find(
      (item) => item.name === name,
    );
    if (!airconService) {
      throw new Error("package not found");
    }
    setBookingState({
      packageDetailsFilter: {
        ...packageDetailsFilter,
        serviceTypes: [airconService.value],
        repeatEvery: [airconService.repeatEvery],
        repeatEveryTimes: airconService.duration,
      },
    });
    push("/booking/aircon-service");
  };

  const onClickService = async () => {
    if (link) {
      sendAnalyticData({
        name: "view_beta_service",
        values: { service_type: name as BetaServiceName },
      });
      const trackingLink = await buildLinkWithTrackingUrl(link);
      handleOpenLink(trackingLink);
      return;
    }
    sendAnalyticData({
      name: "view_service",
      values: { service_type: name as ServiceName | AirconServiceName },
    });
    if (
      name === "general-servicing" ||
      name === "chemical-overhaul" ||
      name === "compressor-wash" ||
      name === "gas-top-up" ||
      name === "chemical-wash"
    ) {
      onClickAirconService(name as AirconServiceName);
      return;
    }
    if (name in bookingRoutes) {
      push(bookingRoutes[name as ServiceName][0]);
    }
  };

  return (
    <ServiceIconUI
      icon={icon}
      title={title}
      onClick={onClickService}
      className={className}
    />
  );
}

export function BetaServiceIcon({
  icon,
  title,
  name,
  link,
  onClick,
  className,
}: BetaServiceItem & {
  className?: string;
  onClick: (name: BetaServiceName, link: string) => void;
}) {
  const onClickService = async () => {
    const trackingLink = await buildLinkWithTrackingUrl(link);
    onClick(name, trackingLink);
  };

  return (
    <ServiceIconUI
      icon={icon}
      title={title}
      onClick={onClickService}
      className={className}
    />
  );
}
